import axios from 'axios';

/**
 * Job Folders ("aplankai") — typed API client.
 *
 * Centralises every call against /api/job-folders/* so pages don't have to
 * remember endpoint paths or response envelopes. All methods unwrap the
 * `{ success, data }` envelope and throw on `success === false`.
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export interface JobFolder {
  id: number;
  employer_id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface JobFolderWithStats extends JobFolder {
  job_count: number;
  published_count: number;
  not_published_count: number;
}

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: string;
  errors?: Record<string, string>;
  message?: string;
}

async function unwrap<T>(promise: Promise<{ data: ApiEnvelope<T> }>): Promise<T> {
  const res = await promise;
  if (!res.data?.success) {
    const err: any = new Error(res.data?.error || 'Request failed');
    err.fieldErrors = res.data?.errors;
    throw err;
  }
  return res.data.data as T;
}

export const jobFoldersApi = {
  list: () =>
    unwrap<{ folders: JobFolderWithStats[]; count: number }>(
      axios.get(`${API_URL}/job-folders`, { headers: authHeaders() }),
    ),

  create: (name: string) =>
    unwrap<{ folder: JobFolder }>(
      axios.post(
        `${API_URL}/job-folders`,
        { name },
        { headers: authHeaders() },
      ),
    ),

  rename: (id: number, name: string) =>
    unwrap<{ folder: JobFolder }>(
      axios.put(
        `${API_URL}/job-folders/${id}`,
        { name },
        { headers: authHeaders() },
      ),
    ),

  remove: (id: number) =>
    unwrap<{ id: number }>(
      axios.delete(`${API_URL}/job-folders/${id}`, { headers: authHeaders() }),
    ),

  /**
   * Bulk-assign a set of jobs to a folder (or detach by passing folderId = null).
   */
  assignJobs: (jobIds: number[], folderId: number | null) =>
    unwrap<{ updated: number; folder_id: number | null }>(
      axios.post(
        `${API_URL}/job-folders/assign`,
        { job_ids: jobIds, folder_id: folderId },
        { headers: authHeaders() },
      ),
    ),
};
